import React, { useEffect, useRef, useState } from 'react';

// ---------- SceneManifest types ----------

export interface ManifestAsset {
  id: string;
  src: string;
  rgba: true;
  w: number;
  h: number;
  metadata?: {
    role: string;
    description: string;
    prompt: string;
    originalBbox?: [number, number, number, number];
    classification?: 'background' | 'foreground-decor' | 'subject-graphic' | 'border-frame' | 'text-overlay' | 'unknown';
    usageSuggestions?: {
      recommendedAnimations: string[];
      layerOrder: 'background' | 'middle' | 'foreground' | 'text-overlay';
      canOverlapText: boolean;
      transparencyIntent: string;
    };
    placementGuidelines?: {
      recommendedAnchors: string[];
      relativeScale: number; // Estimated scale relative to canvas width (e.g. 0.4 = 40%)
      alignmentConstraints: string;
    };
    originalReferenceContext?: {
      relativeZOrder: number;
      spatialIntent: string;
    };
  };
}

export interface ManifestLayer {
  id: string;
  assetId?: string;
  textSlotKey?: string;
  z: number;
  anchor?: string;
  fullBleed?: boolean;
}

export interface ManifestTextSlot {
  key: string;
  z: number;
  default?: string;
  editable: boolean;
  required: boolean;
  dynamic?: string;
  // Layout fields (added in manifest v1.0 rev2)
  x?: number;          // 0–100 percentage of canvas width (50 = centre)
  y?: number;          // 0–100 percentage of canvas height (top edge)
  fontSize?: number;   // canvas pixels @ 1080w
  color?: string;      // hex
  align?: 'left' | 'center' | 'right';
  fontWeight?: 'normal' | 'bold';
}

export interface AnimationBinding {
  target: string;
  type: string;
  provider: string;
  params: Record<string, unknown>;
  supported: boolean;
}

export interface SceneManifest {
  manifestVersion: '1.0';
  themeName: string;
  canvas: { w: number; h: number; safeArea: [number, number, number, number] };
  assets: ManifestAsset[];
  layers: ManifestLayer[];
  textSlots: ManifestTextSlot[];
  bindings: AnimationBinding[];
  provenance: { engine: 'type-b'; generatedAt: string; providers: Record<string, string>; dryRun: boolean };
}

export type TextValues = Record<string, string>;

// ---------- CSS animation keyframes ----------

const KEYFRAMES = `
@keyframes typeb-bloom {
  from { transform: scale(var(--bloom-from, 0.7)); opacity: 0.5; }
  to   { transform: scale(1); opacity: 1; }
}
@keyframes typeb-sway {
  0%,100% { transform: rotate(calc(var(--sway-deg, 3) * 1deg)); }
  50%      { transform: rotate(calc(var(--sway-deg, 3) * -1deg)); }
}
@keyframes typeb-glow {
  0%,100% { opacity: var(--glow-min, 0.4); }
  50%      { opacity: var(--glow-max, 1.0); }
}
@keyframes typeb-drift {
  0%,100% { transform: translate(0, 0); }
  50%      { transform: translate(var(--drift-x, 20px), var(--drift-y, -30px)); }
}
@keyframes typeb-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}
`;

function injectKeyframes() {
  if (document.getElementById('typeb-keyframes')) return;
  const s = document.createElement('style');
  s.id = 'typeb-keyframes';
  s.textContent = KEYFRAMES;
  document.head.appendChild(s);
}

// ---------- Animation binding → CSS ----------

function bindingToCSS(b: AnimationBinding): React.CSSProperties {
  if (!b.supported) return {};
  const p = b.params as Record<string, number>;
  switch (b.type) {
    case 'bloom':
      return {
        '--bloom-from': String(p.from ?? 0.7),
        animation: `typeb-bloom ${p.duration ?? 0.8}s ease-out forwards`,
        animationDelay: `${p.delay ?? 0}s`,
      } as React.CSSProperties;
    case 'sway':
      return {
        '--sway-deg': String(p.deg ?? 3),
        transformOrigin: 'top center',
        animation: `typeb-sway ${(p.periodMs ?? 4000) / 1000}s ease-in-out infinite`,
      } as React.CSSProperties;
    case 'glow':
      return {
        '--glow-min': String(p.minOpacity ?? 0.4),
        '--glow-max': String(p.maxOpacity ?? 1.0),
        animation: `typeb-glow ${(p.periodMs ?? 2600) / 1000}s ease-in-out infinite`,
      } as React.CSSProperties;
    case 'drift':
      return {
        '--drift-x': `${p.dx ?? 20}px`,
        '--drift-y': `${p.dy ?? -30}px`,
        animation: `typeb-drift ${(p.periodMs ?? 6000) / 1000}s ease-in-out infinite`,
      } as React.CSSProperties;
    case 'fade-in':
      return {
        animation: `typeb-fade-in ${p.duration ?? 0.6}s ease-out forwards`,
        animationDelay: `${p.delay ?? 0.1}s`,
        opacity: 0,
      } as React.CSSProperties;
    default:
      return {};
  }
}

function buildAnimationMap(bindings: AnimationBinding[]): Record<string, React.CSSProperties> {
  const map: Record<string, React.CSSProperties> = {};
  for (const b of bindings) {
    map[b.target] = { ...(map[b.target] ?? {}), ...bindingToCSS(b) };
  }
  return map;
}

// ---------- Anchor → absolute position ----------

function anchorStyles(anchor: string | undefined, fullBleed?: boolean): React.CSSProperties {
  if (fullBleed) {
    return { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' as const };
  }
  switch (anchor) {
    case 'top-left':
      return { position: 'absolute', top: 0, left: 0, width: '45%', height: 'auto' };
    case 'top-right':
      return { position: 'absolute', top: 0, right: 0, width: '45%', height: 'auto' };
    case 'bottom-left':
      return { position: 'absolute', bottom: 0, left: 0, width: '45%', height: 'auto' };
    case 'bottom-right':
      return { position: 'absolute', bottom: 0, right: 0, width: '45%', height: 'auto' };
    case 'top-center':
      return { position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '60%', height: 'auto' };
    case 'bottom-center':
      return { position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '60%', height: 'auto' };
    case 'right':
      return { position: 'absolute', top: '28%', right: 0, width: '22%', height: 'auto' };
    case 'left':
      return { position: 'absolute', top: '28%', left: 0, width: '22%', height: 'auto' };
    case 'center':
      return { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '60%', height: 'auto' };
    default:
      return { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '30%', height: 'auto' };
  }
}

// ---------- Text slot positioning ----------

// Fallback vertical distribution for old manifests without y/fontSize fields
function fallbackTextLayout(slots: ManifestTextSlot[]): Map<string, { y: number; fontSize: number; color: string }> {
  const KNOWN: Record<string, { y: number; fontSize: number; color: string }> = {
    mantraHeader:  { y: 16, fontSize: 44, color: '#8b6248' },
    coupleNames:   { y: 40, fontSize: 96, color: '#2d1e0e' },
    weddingTitle:  { y: 40, fontSize: 96, color: '#2d1e0e' },
    names:         { y: 40, fontSize: 96, color: '#2d1e0e' },
    countdownDays: { y: 53, fontSize: 52, color: '#8b6248' },
    countdown:     { y: 53, fontSize: 52, color: '#8b6248' },
    eventDate:     { y: 62, fontSize: 42, color: '#2d1e0e' },
    date:          { y: 62, fontSize: 42, color: '#2d1e0e' },
    venue:         { y: 70, fontSize: 38, color: '#2d1e0e' },
    tagline:       { y: 76, fontSize: 34, color: '#8b6248' },
    mantraFooter:  { y: 89, fontSize: 36, color: '#8b6248' },
  };

  const map = new Map<string, { y: number; fontSize: number; color: string }>();
  const unknown = slots.filter((s) => !KNOWN[s.key]);
  const step = unknown.length > 1 ? 54 / (unknown.length - 1) : 0;
  let idx = 0;

  for (const s of slots) {
    if (KNOWN[s.key]) {
      map.set(s.key, KNOWN[s.key]);
    } else {
      map.set(s.key, { y: 28 + idx++ * step, fontSize: 44, color: '#2d1e0e' });
    }
  }
  return map;
}

// ---------- Main component ----------

interface TypeBRendererProps {
  manifest: SceneManifest;
  textValues?: TextValues;
  scale?: number;
  className?: string;
}

export function TypeBRenderer({ manifest, textValues = {}, scale = 1, className = '' }: TypeBRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [computedScale, setComputedScale] = useState(scale);

  useEffect(() => { injectKeyframes(); }, []);

  useEffect(() => {
    if (scale !== 1) return;
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([e]) => {
      const w = e.contentRect.width;
      if (w > 0) setComputedScale(w / manifest.canvas.w);
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [scale, manifest.canvas.w]);

  const animMap = buildAnimationMap(manifest.bindings);
  const assetById = Object.fromEntries(manifest.assets.map((a) => [a.id, a]));
  const sortedLayers = [...manifest.layers].sort((a, b) => a.z - b.z);

  const canvasW = manifest.canvas.w;
  const canvasH = manifest.canvas.h;

  // Build fallback layout for any text slot missing y/fontSize
  const fallback = fallbackTextLayout(manifest.textSlots);

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      style={{ width: '100%', aspectRatio: `${canvasW} / ${canvasH}` }}
    >
      {/* Scaled inner canvas — full 1080×1920, scaled to fit container */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: canvasW,
          height: canvasH,
          transform: `scale(${computedScale})`,
          transformOrigin: 'top left',
        }}
      >
        {/* ── Image layers ── */}
        {sortedLayers.map((layer) => {
          const asset = layer.assetId ? assetById[layer.assetId] : null;
          if (!asset) return null;

          const anim = animMap[layer.id] ?? {};
          const pos = anchorStyles(layer.anchor, layer.fullBleed);

          return (
            <img
              key={layer.id}
              src={asset.src}
              alt=""
              draggable={false}
              style={{ ...pos, ...anim, zIndex: layer.z }}
              onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
            />
          );
        })}

        {/* ── Text slots ── */}
        {manifest.textSlots.map((slot) => {
          const value = textValues[slot.key] ?? slot.default ?? '';
          if (!value) return null;

          // Prefer manifest-supplied layout; fall back to built-in table
          const fb = fallback.get(slot.key)!;
          const pctX  = slot.x        ?? 50;
          const pctY  = slot.y        ?? fb.y;
          const fSize = slot.fontSize  ?? fb.fontSize;
          const color = slot.color     ?? fb.color;
          const align = slot.align     ?? 'center';
          const fw    = slot.fontWeight ?? 'normal';

          // x: 50 → centred; x: 0 → left-anchored; x: 100 → right-anchored
          const xTranslate = pctX === 50 ? '-50%' : pctX === 0 ? '0%' : '-100%';

          return (
            <div
              key={slot.key}
              style={{
                position: 'absolute',
                left: `${pctX}%`,
                top: `${pctY}%`,
                transform: `translateX(${xTranslate})`,
                zIndex: slot.z,
                fontSize: fSize,
                color,
                fontWeight: fw,
                textAlign: align,
                fontFamily: 'Georgia, "Times New Roman", serif',
                letterSpacing: fw === 'bold' ? '0.05em' : '0.12em',
                lineHeight: 1.2,
                whiteSpace: 'pre-line',
                textShadow: '0 1px 3px rgba(0,0,0,0.15)',
                padding: '0 40px',
                width: '100%',
                boxSizing: 'border-box' as const,
              }}
            >
              {value}
            </div>
          );
        })}
      </div>
    </div>
  );
}
